import { useRef, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { HpApiWebApiError } from '@zg-rentals/ts-hotpads-api-client';
import { v3ErrorReason } from '@/src/lib/api/client/hotpadsApiV3Client';
import { useCurrentUser, useSendOtp } from '@/src/lib/hooks/query';
import { SEND_BLOCKED_REASONS } from './otpReasons';

/** What the code step shows: where the code went, and whether another can be sent. */
export type OtpCodeStep = {
  email: string;
  sendLimitReached: boolean;
};

export type RequireSignInResult =
  /** Already signed in: `onSignedIn` ran before this resolved. */
  | { status: 'signedIn' }
  /** A code is out (or an earlier one is redeemable) and the step is showing. */
  | { status: 'codeStep' }
  /** Identity is still resolving or a send is in flight; nothing happened. */
  | { status: 'notReady' }
  /** No code went out and none is redeemable; show `message` on the caller's own form. */
  | { status: 'sendFailed'; field: 'email' | 'form'; message: string };

export type UseOtpSignInGateOptions<TPayload> = {
  /**
   * The gated action. Always the latest render's function, so it reads current data; anything
   * that must stay as it was at submit (the form's values, a submission id) goes in the payload.
   * A returned promise is awaited by `requireSignIn` and `handleVerified`.
   */
  onSignedIn: (payload: TPayload) => void | Promise<void>;
  /** Lets a host that owns chrome (a modal header, footer actions) react to the step. */
  onOpenChange?: (open: boolean) => void;
};

export type OtpSignInGateState = Pick<
  ReturnType<typeof useOtpSignInGate>,
  'codeStep' | 'handleVerified' | 'handleBack'
>;

/**
 * Makes an email-code sign-in a precondition of an action. Render the step with `OtpSignInGate`.
 *
 * `requireSignIn(email, payload)` runs `onSignedIn(payload)` at once for a signed-in renter. For a
 * signed-out one it sends the first code and opens the step; `onSignedIn` runs once the code
 * verifies, and going back drops the payload. The first code is sent here, from the caller's
 * submit handler, because an RQ5 mutate() fired from the step's mount effect is orphaned under
 * StrictMode.
 */
export function useOtpSignInGate<TPayload>({ onSignedIn, onOpenChange }: UseOtpSignInGateOptions<TPayload>) {
  const t = useTranslations('auth');
  const { data: currentUser } = useCurrentUser();
  const sendOtp = useSendOtp();
  const [codeStep, setCodeStep] = useState<OtpCodeStep | null>(null);
  // Boxed so an `undefined` payload still reads as "pending".
  const pendingRef = useRef<{ payload: TPayload } | null>(null);
  const runSignedIn = useMemoizedFn(onSignedIn);
  const notifyOpenChange = useMemoizedFn((open: boolean) => onOpenChange?.(open));

  const sendFailed = (error: unknown): RequireSignInResult => {
    switch (v3ErrorReason(error)) {
      case HpApiWebApiError.INVALID_EMAIL:
        return { status: 'sendFailed', field: 'email', message: t('otpInitialSendInvalidEmail') };
      case HpApiWebApiError.ACCOUNT_DEACTIVATED:
        return { status: 'sendFailed', field: 'email', message: t('otpInitialSendAccountDeactivated') };
      // Both OTP endpoints 404 while the emailOtpSettings.v3EndpointsEnabled kill switch is off.
      case HpApiWebApiError.NOT_FOUND:
        return { status: 'sendFailed', field: 'form', message: t('otpInitialSendUnavailable') };
      default:
        return { status: 'sendFailed', field: 'form', message: t('otpInitialSendFailed') };
    }
  };

  const requireSignIn = async (email: string, payload: TPayload): Promise<RequireSignInResult> => {
    // `undefined` is identity still resolving, not a signed-out visitor: sending a code now
    // could ask a signed-in renter to sign in again.
    if (currentUser === undefined || sendOtp.isPending) return { status: 'notReady' };

    if (currentUser?.loggedIn) {
      await runSignedIn(payload);
      return { status: 'signedIn' };
    }

    let sendLimitReached = false;
    try {
      await sendOtp.mutateAsync({ postData: { email } });
    } catch (error) {
      const reason = v3ErrorReason(error);
      if (!reason || !SEND_BLOCKED_REASONS.has(reason)) return sendFailed(error);
      // No new code is coming, but an earlier one may still be sitting in their inbox.
      sendLimitReached = true;
    }

    pendingRef.current = { payload };
    setCodeStep({ email, sendLimitReached });
    notifyOpenChange(true);
    return { status: 'codeStep' };
  };

  const close = () => {
    setCodeStep(null);
    notifyOpenChange(false);
  };

  const handleVerified = async () => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    close();
    if (pending) await runSignedIn(pending.payload);
  };

  const handleBack = () => {
    pendingRef.current = null;
    close();
  };

  return {
    codeStep,
    isSending: sendOtp.isPending,
    requireSignIn,
    handleVerified,
    handleBack,
  };
}
